import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import client from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    client.get(`/projects/${slug}`).then(({ data }) => setP(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <p className="font-display text-3xl text-white">Product not found</p>
        <Link to="/technology" className="btn-ghost rounded-full px-6 py-3 font-mono2 text-xs uppercase tracking-widest">Back to Technology</Link>
      </div>
    );
  }
  if (!p) return <div className="min-h-screen" />;

  const accent = p.accent || "#d4af37";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: p.name,
    applicationCategory: p.industry,
    description: p.description,
    creator: { "@type": "Organization", name: "ImJove Tech" },
  };

  return (
    <div>
      <SEO title={p.seo_title || `${p.name} — ImJove Tech`} description={p.seo_description || p.description} image={p.images?.[0]} jsonLd={jsonLd} />
      <section className="relative overflow-hidden pb-20 pt-36">
        <div className="pointer-events-none absolute -top-20 right-0 h-96 w-96 rounded-full opacity-20 blur-[120px]" style={{ background: accent }} />
        <ParticleField className="absolute inset-0 z-0" density={30} />
        <div className="relative z-10 mx-auto max-w-[1100px] px-6 md:px-12">
          <button onClick={() => navigate(-1)} className="mb-8 inline-flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-widest text-white/40 hover:text-white" data-testid="product-back">
            <ArrowLeft size={13} /> Back
          </button>
          <Reveal>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl font-display text-3xl font-bold" style={{ background: `${accent}1a`, color: accent, border: `1px solid ${accent}40` }}>
                {p.logo ? <img src={p.logo} alt={p.name} className="h-full w-full object-cover" /> : p.name.charAt(0)}
              </div>
              <div>
                <StatusBadge status={p.status} size="lg" />
                <h1 className="mt-2 font-display text-5xl font-bold tracking-tighter text-white sm:text-6xl">{p.name}</h1>
              </div>
            </div>
            <p className="mt-6 font-display text-2xl font-light" style={{ color: accent }}>{p.tagline}</p>
            <p className="mt-5 max-w-2xl font-body text-lg leading-relaxed text-white/60">{p.long_description || p.description}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              {p.website_url && (
                <a href={p.website_url} target="_blank" rel="noreferrer" data-testid="product-launch" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-mono2 text-[12px] font-semibold uppercase tracking-widest text-black" style={{ background: accent }}>
                  {p.cta_label || "Launch Platform"} <ArrowUpRight size={15} />
                </a>
              )}
              <Link to="/contact" className="btn-ghost inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-mono2 text-[12px] uppercase tracking-widest">Partner With Us</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-28">
        <div className="mx-auto grid max-w-[1100px] gap-6 px-6 md:grid-cols-3 md:px-12">
          <Reveal className="md:col-span-2">
            <div className="h-full rounded-2xl border border-white/10 bg-ink-800 p-8">
              <h3 className="font-mono2 text-xs uppercase tracking-widest text-white/40">Capabilities</h3>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {(p.features || []).map((f) => (
                  <div key={f} className="flex items-start gap-3">
                    <Check size={16} className="mt-0.5 shrink-0" style={{ color: accent }} />
                    <span className="font-body text-white/70">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-2xl border border-white/10 bg-ink-800 p-8">
              <h3 className="font-mono2 text-xs uppercase tracking-widest text-white/40">Details</h3>
              <dl className="mt-6 space-y-4 font-body text-sm">
                <div><dt className="text-white/40">Industry</dt><dd className="text-white/80">{p.industry}</dd></div>
                <div><dt className="text-white/40">Status</dt><dd className="text-white/80">{p.status}</dd></div>
                <div><dt className="text-white/40">Founder</dt><dd className="text-white/80">{p.founder}</dd></div>
                {p.technology?.length > 0 && (
                  <div><dt className="text-white/40">Technology</dt><dd className="text-white/80">{p.technology.join(", ")}</dd></div>
                )}
              </dl>
              <div className="mt-6 flex items-center gap-2 border-t border-white/10 pt-5">
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-gradient-to-br from-gold-dark via-gold to-gold-light text-[8px] font-bold text-black font-display">J</span>
                <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-white/40">Powered by ImJove Tech</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {(p.images?.length > 0 || p.videos?.length > 0) && (
        <section className="pb-28">
          <div className="mx-auto max-w-[1100px] px-6 md:px-12">
            <Reveal>
              <h3 className="font-mono2 text-xs uppercase tracking-widest text-white/40">Media</h3>
              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                {(p.images || []).map((src, i) => (
                  <img key={`img-${i}`} src={src} alt={`${p.name} ${i + 1}`} data-testid={`product-image-${i}`} className="w-full rounded-2xl border border-white/10 object-cover" />
                ))}
                {(p.videos || []).map((src, i) => (
                  <video key={`vid-${i}`} src={src} controls data-testid={`product-video-${i}`} className="w-full rounded-2xl border border-white/10" />
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}
    </div>
  );
}
